/* eslint-disable max-lines */
import { FC, useCallback, useMemo, useState } from 'react';

import CopyAllOutlined from '@mui/icons-material/CopyAllOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {
  Grid,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import json from 'react-syntax-highlighter/dist/cjs/languages/hljs/json';
import Syntax from 'react-syntax-highlighter/dist/cjs/light';
import atomOneDarkReasonable from 'react-syntax-highlighter/dist/cjs/styles/hljs/atom-one-dark-reasonable';

import { SyntaxHighlighterDialog } from './SyntaxHighlighterDialog';

Syntax.registerLanguage('json', json);

type SyntaxHighlighterProps = {
  code: string;
  syntaxLanguage?: string;
  title?: string;
  sx?: SxProps<Theme>;
  dialogSx?: SxProps<Theme>;
  syntaxStyles?: React.CSSProperties;
  allowExpand?: boolean;
  onCopySuccess?: () => void;
  onDownloadRequested?: () => void;
  downloadTooltip?: string;
  copyTooltip?: string;
  expandTooltip?: string;
};

const ICON_HEIGHT = '16px';

export const SyntaxHighlighter: FC<SyntaxHighlighterProps> = ({
  code,
  title,
  syntaxLanguage,
  allowExpand = false,
  sx = {},
  dialogSx = {},
  syntaxStyles = {},
  onCopySuccess,
  onDownloadRequested,
  downloadTooltip,
  copyTooltip,
  expandTooltip,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();

  const syntaxStylesWithDefaults = useMemo<React.CSSProperties>(
    () => ({
      fontSize: '12px',
      overflowY: 'auto',
      maxHeight: '400px',
      margin: 0,
      padding: theme.spacing(2),
      ...syntaxStyles,
      paddingTop: title ? theme.spacing(4) : theme.spacing(2),
    }),
    [syntaxStyles, theme, title],
  );

  const onCopy = useCallback(() => {
    navigator.clipboard.writeText(code);
    onCopySuccess?.();
  }, [code, onCopySuccess]);

  const onClose = useCallback(() => setIsExpanded(false), []);

  return (
    <>
      <Stack sx={{ position: 'relative', ...sx }}>
        <Typography
          sx={{
            position: 'absolute',
            top: theme.spacing(2),
            transform: 'translateY(-50%)',
            left: theme.spacing(2),
            fontSize: '14px',
            lineHeight: 1,
          }}
          color="grey.300"
        >
          {title}
        </Typography>
        <Syntax
          language={syntaxLanguage ?? 'json'}
          style={atomOneDarkReasonable}
          showLineNumbers
          wrapLongLines
          customStyle={syntaxStylesWithDefaults}
          data-testid="syntax"
        >
          {code}
        </Syntax>
        <Grid
          container
          sx={{
            position: 'absolute',
            bottom: '16px',
            justifyContent: 'flex-end',
            px: 1,
          }}
        >
          {onDownloadRequested && (
            <IconButton
              size="small"
              sx={{ height: ICON_HEIGHT, color: 'white', p: 0 }}
              onClick={onDownloadRequested}
              title={downloadTooltip ?? ''}
            >
              <DownloadOutlined sx={{ height: ICON_HEIGHT, p: 0 }} />
            </IconButton>
          )}
          <IconButton
            size="small"
            sx={{ height: ICON_HEIGHT, color: 'white', p: 0 }}
            onClick={onCopy}
            title={copyTooltip ?? ''}
          >
            <CopyAllOutlined sx={{ height: ICON_HEIGHT, p: 0 }} />
          </IconButton>

          {allowExpand && (
            <IconButton
              size="small"
              sx={{ height: ICON_HEIGHT, color: 'white', p: 0 }}
              onClick={() => {
                setIsExpanded(!isExpanded);
              }}
              title={expandTooltip ?? ''}
            >
              <OpenInFullIcon sx={{ height: ICON_HEIGHT }} />
            </IconButton>
          )}
        </Grid>
      </Stack>
      {allowExpand && (
        <SyntaxHighlighterDialog
          isOpen={isExpanded}
          onClose={onClose}
          title={title}
          syntaxLanguage={syntaxLanguage}
          code={code}
          dialogSx={dialogSx}
          syntaxStyles={syntaxStylesWithDefaults}
          onCopy={onCopy}
          onDownloadRequested={onDownloadRequested}
          copyTooltip={copyTooltip}
          downloadTooltip={downloadTooltip}
        />
      )}
    </>
  );
};
